import styled from 'foundations-theming/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { css, mediaQuery } from '../../utils/Styling';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ScrollTrack = styled.div.withConfig({
  displayName: "ACHorizontalScrollContainer__ScrollTrack",
  componentId: "sc-77vi53-0"
})(["margin-top:8px;position:relative;", ""], mediaQuery('mobile', css(["display:none !important;"])));
const ScrollThumb = styled.div.withConfig({
  displayName: "ACHorizontalScrollContainer__ScrollThumb",
  componentId: "sc-77vi53-1"
})(["height:10px;width:80px;top:-10px;border-radius:5px;background:", ";position:absolute;cursor:grab;"], ({
  theme
}) => theme.color['do-not-use-battleship']);
const Scrollbar = ({
  scrollableContentRef
}) => {
  const scrollTrackRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const dragStartClientX = useRef(0);
  const initialScrollLeft = useRef(0);
  const originalCursor = useRef(document.body.style.cursor);
  const [isDragging, setIsDragging] = useState(false);
  const handleScroll = useCallback(() => {
    if (!scrollableContentRef.current || !scrollTrackRef.current || !scrollThumbRef.current) {
      return;
    }
    const left = scrollableContentRef.current.scrollLeft;
    const maxLeft = scrollableContentRef.current.scrollWidth - scrollableContentRef.current.offsetWidth;
    const thumbPosition = left / maxLeft * (scrollTrackRef.current.offsetWidth - scrollThumbRef.current.offsetWidth);
    scrollThumbRef.current.style.left = `${thumbPosition}px`;
    scrollTrackRef.current.style.display = maxLeft ? 'block' : 'none';
  }, [scrollableContentRef]);
  const handleMouseDown = useCallback(e => {
    var _scrollableContentRef;
    e.preventDefault();
    e.stopPropagation();
    dragStartClientX.current = e.clientX;
    initialScrollLeft.current = ((_scrollableContentRef = scrollableContentRef.current) === null || _scrollableContentRef === void 0 ? void 0 : _scrollableContentRef.scrollLeft) || 0;
    document.body.style.cursor = 'grabbing';
    setIsDragging(true);
  }, [scrollableContentRef]);
  const handleMouseUp = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    document.body.style.cursor = originalCursor.current;
    setIsDragging(false);
  }, []);
  const handleMouseMove = useCallback(e => {
    if (!scrollableContentRef.current || !scrollTrackRef.current || !scrollThumbRef.current) {
      return;
    }
    const maxLeftContent = scrollableContentRef.current.scrollWidth - scrollableContentRef.current.offsetWidth;
    const maxLeftTrack = scrollTrackRef.current.offsetWidth - scrollThumbRef.current.offsetWidth;
    const scaledDeltaX = (e.clientX - dragStartClientX.current) * (maxLeftContent / maxLeftTrack);
    scrollableContentRef.current.scrollLeft = Math.min(initialScrollLeft.current + scaledDeltaX, scrollableContentRef.current.scrollWidth - scrollableContentRef.current.offsetWidth);
  }, [scrollableContentRef]);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
    return () => {};
  }, [isDragging, handleMouseMove]);
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseUp]);
  useEffect(() => {
    if (scrollableContentRef.current && scrollTrackRef.current) {
      const scrollable = scrollableContentRef.current;
      const scrollableChild = scrollableContentRef.current.firstChild;
      const observer = new ResizeObserver(handleScroll);
      scrollable.addEventListener('scroll', handleScroll);
      observer.observe(scrollable);
      observer.observe(scrollableChild);
      return () => {
        scrollable.removeEventListener('scroll', handleScroll);
        observer.unobserve(scrollable);
        observer.unobserve(scrollableChild);
      };
    }
    return () => {};
  }, [scrollableContentRef, handleScroll]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(ScrollTrack, {
      ref: scrollTrackRef,
      children: /*#__PURE__*/_jsx(ScrollThumb, {
        onMouseDown: handleMouseDown,
        ref: scrollThumbRef
      })
    })
  );
};
const ScrollableContainer = styled.div.withConfig({
  displayName: "ACHorizontalScrollContainer__ScrollableContainer",
  componentId: "sc-77vi53-2"
})(["overflow-x:auto;scrollbar-color:transparent transparent;scrollbar-width:none;-ms-overflow-style:none;&::-webkit-scrollbar{background:transparent;display:none;}&::-webkit-scrollbar-thumb{background:transparent !important;}"]);
const ACHorizontalScrollContainer = ({
  children,
  className,
  style
}) => {
  const scrollableContentRef = useRef(null);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(ScrollableContainer, {
      className: className,
      ref: scrollableContentRef,
      style: style,
      children: children
    }), /*#__PURE__*/_jsx(Scrollbar, {
      scrollableContentRef: scrollableContentRef
    })]
  });
};
export default ACHorizontalScrollContainer;